import { useCallback, useEffect, useRef, useState } from 'react';

import { showAlert } from 'components/shared/Alert/Alert';

import {
  getErrorMessage,
  isNegativeAmount,
  parseDecimalEntry,
  toDecimalDraft
} from '../lib/helpers';
import {
  ALERT_DURATION,
  ALLOWED_KEYS,
  DEBOUNCE_TIME,
  DECIMAL_SEPARATOR_KEY,
  MAX_PERCENT_VALUE,
  PERCENT_DIVISOR
} from '../lib/constants';

const useCurrencyValue = ({
  value,
  isPercent,
  isStylePercent,
  currentUnit,
  minValue,
  maxValue,
  symbol,
  validationMessages,
  allowNegative,
  decimalEntry,
  onValueChange,
  onBlur,
  getInput
}) => {
  const timeoutRef = useRef(null);
  const [inputValue, setInputValue] = useState(value);
  const [isNegative, setIsNegative] = useState(isNegativeAmount(value));
  const [decimalDraft, setDecimalDraft] = useState(null);
  const canBeNegative = allowNegative && !isPercent;
  const isDecimalEntry = decimalEntry && !isPercent;

  useEffect(() => {
    setInputValue(value);
    setIsNegative(isNegativeAmount(value));
  }, [value, isPercent]);

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const isValidAmount = useCallback(
    (amount) => {
      const isPositive = canBeNegative || amount >= 0;
      const isAboveMin = minValue === undefined || amount >= minValue;
      const isBelowMax = isPercent
        ? amount <= Math.min(maxValue ?? MAX_PERCENT_VALUE, MAX_PERCENT_VALUE)
        : maxValue === undefined || amount <= maxValue;
      return isPositive && isAboveMin && isBelowMax;
    },
    [minValue, maxValue, isPercent, canBeNegative]
  );

  const updateValue = useCallback(
    (newValue) => {
      if (!isValidAmount(newValue)) {
        showAlert({
          color: 'danger',
          message: getErrorMessage(minValue, maxValue, symbol, validationMessages),
          duration: ALERT_DURATION
        });

        const value = newValue > maxValue ? maxValue : !!minValue ? minValue : 0;
        setInputValue(value);
        setDecimalDraft((draft) => (draft === null ? null : toDecimalDraft(value)));
        onValueChange(value);
        return value;
      }
      return newValue;
    },
    [isValidAmount, minValue, maxValue, symbol, onValueChange, validationMessages]
  );

  const debounceValueChange = useCallback(
    (value) => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        updateValue(value);
      }, DEBOUNCE_TIME);
    },
    [updateValue]
  );

  const handleDecimalEntryChange = (text) => {
    const entry = parseDecimalEntry(text);
    if (!entry) return;

    setDecimalDraft(entry.draft);
    setIsNegative(false);
    setInputValue(entry.cents);
    onValueChange(entry.cents);
    debounceValueChange(entry.cents);
  };

  const handleValueChange = (e) => {
    e.preventDefault();
    const { value } = e.target;

    if (isDecimalEntry) {
      handleDecimalEntryChange(value);
      return;
    }

    const numericValue = value.replace(/[^0-9]/g, '');
    const newValue = numericValue ? parseInt(numericValue, 10) : 0;

    const isBackspaceDelete = e.nativeEvent.inputType === 'deleteContentBackward';
    const shouldHandlePercentDelete = isBackspaceDelete && numericValue.length > 0 && isPercent;

    if (shouldHandlePercentDelete) {
      const truncatedValue = numericValue.slice(0, -1);
      const formattedValue = !isNaN(truncatedValue) ? truncatedValue : '';

      setInputValue(formattedValue);
      onValueChange(formattedValue);
      debounceValueChange(formattedValue);
      return;
    }

    if (isPercent) {
      const percentageValue = Math.min(newValue, MAX_PERCENT_VALUE) / PERCENT_DIVISOR;
      const calculatedValue = (percentageValue * MAX_PERCENT_VALUE) / PERCENT_DIVISOR;
      const newPercentageValue = isStylePercent ? percentageValue : calculatedValue;
      const newInputValue = currentUnit ? newPercentageValue : newPercentageValue * PERCENT_DIVISOR;

      setInputValue(newInputValue);
      onValueChange(newInputValue);
      return;
    }

    const signedValue = canBeNegative && newValue && value.includes('-') ? -newValue : newValue;
    setIsNegative(signedValue < 0);
    setInputValue(signedValue);
    onValueChange(signedValue);
    debounceValueChange(signedValue);
  };

  const toggleSign = () => {
    const signedValue = -Number(inputValue || 0);
    setIsNegative(!isNegative);
    setInputValue(signedValue);
    onValueChange(signedValue);
  };

  const handleKeyDown = (e) => {
    if (canBeNegative && !isDecimalEntry && e.key === '-') {
      e.preventDefault();
      toggleSign();
      return;
    }
    const isDecimalSeparator = isDecimalEntry && e.key === DECIMAL_SEPARATOR_KEY;
    if (!ALLOWED_KEYS.test(e.key) && !isDecimalSeparator) {
      e.stopPropagation();
      e.preventDefault();
    }
    const input = getInput();
    if ((e.ctrlKey || e.metaKey) && e.key === 'a' && input) {
      input.select();
    }
  };

  const handleFocus = (e) => {
    const { target } = e;
    if (isDecimalEntry) {
      setDecimalDraft(toDecimalDraft(inputValue));
      requestAnimationFrame(() => target.select());
      return;
    }

    requestAnimationFrame(() => {
      const length = target.value.length;
      target.setSelectionRange(length, length);
    });
  };

  const handleBlur = () => {
    clearTimeout(timeoutRef.current);
    setDecimalDraft(null);
    const newValue = updateValue(inputValue);
    onBlur(newValue);
  };

  return {
    inputValue,
    decimalDraft,
    showNegativeSign: canBeNegative && isNegative,
    canBeNegative,
    handleValueChange,
    handleKeyDown,
    handleFocus,
    handleBlur
  };
};

export default useCurrencyValue;
